import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { DropdownCategory } from "@/api";
import { getDropdownCategoryLabel } from "@/pages/system-settings/tabs/dropdowns/DropdownsSettings.utils";
import { useTranslation } from "@/translations";

const CATEGORY_PARAM = "category";
// DropdownCategory enum has members, so Object.values is non-empty.
const CATEGORIES = Object.values(DropdownCategory) as [DropdownCategory, ...DropdownCategory[]];

function parseCategoryParam(value: string | null): DropdownCategory | null {
  if (value && Object.values(DropdownCategory).includes(value as DropdownCategory)) {
    return value as DropdownCategory;
  }
  return null;
}

export function useSortedCategories() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortedCategories = useMemo(() => {
    const labeled = CATEGORIES.map((category) => ({
      category,
      label: getDropdownCategoryLabel(category, t),
    }));
    labeled.sort((a, b) => a.label.localeCompare(b.label, i18n.language));
    return labeled.map((item) => item.category);
  }, [t, i18n.language]);

  const selectedCategory: DropdownCategory =
    parseCategoryParam(searchParams.get(CATEGORY_PARAM)) ?? sortedCategories[0] ?? CATEGORIES[0];

  const setSelectedCategory = (category: DropdownCategory) => {
    setSearchParams({ [CATEGORY_PARAM]: category });
  };

  return { sortedCategories, selectedCategory, setSelectedCategory };
}
